// Pure decision logic behind the ship-all / stage-all timeout recovery in
// SalesOrderPage.
//
// Extracted from the component so it can be unit-tested against real plan
// payloads. The failure mode this guards against is SILENT: if these
// predicates key off a field the API doesn't actually return, the recovery
// never matches, and a shipment that fully went out is still reported to the
// operator as a failure — exactly the bug being fixed.

/**
 * True when an error means "we don't know whether the server ran the work",
 * rather than "the server looked at this and refused it".
 *
 * The fetch call sites tag deliberate 4xx refusals (bad PIN, nothing Ready,
 * plan already Complete) with serverRejected = true. Everything else — a raw
 * fetch() TypeError from a dropped connection, a 5xx, an API-gateway 504 —
 * leaves it undefined and must be reconciled against real server state
 * before we tell the operator anything.
 */
export function isTransportFailure(err) {
  return !err?.serverRejected;
}

/**
 * Which of `palletIds` have reached one of `terminalStatuses` in this plan
 * snapshot. Keys off pallets[].pallet_id and pallets[].status — the shape
 * GET /packaging/plans/by-so/{sales_order_id} actually returns.
 */
export function landedPalletIds(snapshot, palletIds, terminalStatuses) {
  const pallets = (snapshot && snapshot.pallets) || [];
  const byId = new Map(pallets.map((p) => [p.pallet_id, p]));
  return palletIds.filter((id) => terminalStatuses.includes(byId.get(id)?.status));
}

/**
 * Success verdict: every pallet we acted on landed. An empty target list is
 * never a success — there is nothing to have succeeded at.
 */
export function allPalletsLanded(snapshot, palletIds, terminalStatuses) {
  if (!palletIds || !palletIds.length) return false;
  return landedPalletIds(snapshot, palletIds, terminalStatuses).length === palletIds.length;
}
